import { Component, Input, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import {
  AuctionEvent,
  TranslatableString,
  getTranslation,
} from '../../models/auction-event.model';
import { AuctionModalComponent } from './auction-modal/auction-modal.component';
import { BuyerAuthService } from '../../../../../buyer/buyer-core/buyer-authentication/services/buyer-auth/buyer-auth.service';
import { AuctionInterestApiService } from '../../services/auction-interest-api.service';
import { ToastService } from 'src/app/Shared/services/toast/toast.service';
import {
  TranslateModule,
  TranslateService as NgxTranslateService,
} from '@ngx-translate/core';
import { TimeZoneService } from 'src/app/Shared/services/time-zone/time-zone.service';

@Component({
    selector: 'app-timeline-view',
        standalone: true,
imports: [CommonModule, AuctionModalComponent, TranslateModule],
    templateUrl: './timeline-view.component.html',
    styleUrl: './timeline-view.component.scss'
})
export class TimelineViewComponent implements OnInit, OnChanges, OnDestroy {
  @Input() auctions: AuctionEvent[] = [];
  @Input() locale: string = 'en';
  @Input() appDir: string = 'ltr';

  sortedAuctions: AuctionEvent[] = [];
  selectedAuction: AuctionEvent | null = null;
  showModal: boolean = false;
  isLoggedIn: boolean = false;

  private authSubscription?: Subscription;

  constructor(
    private auth: BuyerAuthService,
    private interestApiService: AuctionInterestApiService,
    private toastService: ToastService,
    private ngxTranslate: NgxTranslateService,
    private timezoneService: TimeZoneService
  ) {}

  ngOnInit() {
    this.sortAuctions();

    // Subscribe to authentication state
    this.authSubscription = this.auth.loggedIn_authorized$.subscribe(
      (isLoggedIn) => {
        this.isLoggedIn = isLoggedIn;
      }
    );
  }

  ngOnChanges() {
    this.sortAuctions();
  }

  ngOnDestroy() {
    // Clean up subscription
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
  }

  sortAuctions() {
    this.sortedAuctions = [...this.auctions].sort(
      (a, b) => a.date.getTime() - b.date.getTime()
    );
  }

  openAuctionModal(auction: AuctionEvent) {
    this.selectedAuction = auction;
    this.showModal = true;
  }

  closeModal() {
    this.showModal = false;
    this.selectedAuction = null;
  }

  formatDate(date: Date): string {
    return this.timezoneService.formatDateToLocale(date, this.locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  formatDay(date: Date): string {
    return this.timezoneService.formatDateToLocale(date, this.locale, { day: 'numeric' });
  }

  formatShortMonth(date: Date): string {
    return this.timezoneService.formatDateToLocale(date, this.locale, { month: 'short' });
  }

  formatTime(timeString: string | null | undefined): string {
    if (!timeString) return '';
    try {
      const today = new Date();
      const dateTimeStr = `${today.toDateString()} ${timeString}`;
      const date = new Date(dateTimeStr);
      if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString(this.locale, {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
      }
      return timeString;
    } catch {
      return timeString;
    }
  }

  getStatusClass(status: string): string {
    return `status-${status}`;
  }

  toggleInterested(auctionId: number, event: Event) {
    event.stopPropagation(); // Prevent card click events
    if (!this.isLoggedIn) return;

    // Find the auction
    const auction = this.sortedAuctions.find((a) => a.id === auctionId);
    if (!auction) return;

    // Store previous state for rollback
    const previousState = auction.isInterested;

    // Optimistic update: Toggle immediately
    auction.isInterested = !auction.isInterested;

    // Determine which API to call
    const apiCall = auction.isInterested
      ? this.interestApiService.markInterested(auctionId)
      : this.interestApiService.removeInterested(auctionId);

    // Call API
    apiCall.subscribe({
      next: () => {
        // Success: Show success toast with dynamic translation
        const successKey = auction.isInterested
          ? 'auctions_calendar.timeline.interest_marked_success'
          : 'auctions_calendar.timeline.interest_removed_success';
        const successMessage = this.ngxTranslate.instant(successKey);

        this.toastService.showInfo(successMessage);
      },
      error: (error) => {
        // Error: Revert optimistic update
        auction.isInterested = previousState;

        // Show error toast with dynamic translation
        const errorMessage =
          error.message ||
          this.ngxTranslate.instant(
            'auctions_calendar.timeline.interest_error'
          );
        this.toastService.showError(errorMessage);
      },
    });
  }

  isInterested(auctionId: number): boolean {
    const auction = this.sortedAuctions.find((a) => a.id === auctionId);
    return auction?.isInterested || false;
  }

  getTranslatedStatus(status: string): string {
    const statusKey = status.toLowerCase();
    return this.ngxTranslate.instant(
      'auctions_calendar.timeline.status.' + statusKey
    );
  }

  getInterestedTitle(): string {
    return this.isLoggedIn
      ? this.ngxTranslate.instant('auctions_calendar.timeline.interested')
      : this.ngxTranslate.instant('auctions_calendar.timeline.sign_in_to_save');
  }

  getViewAuctionText(): string {
    return this.ngxTranslate.instant('auctions_calendar.timeline.view_auction');
  }

  /**
   * Get translated text from a translatable field
   */
  getTranslatedField(field: TranslatableString | string | undefined): string {
    return getTranslation(field, this.locale.split('-')[0]);
  }
}
